import type { MouseEvent, PropsWithChildren } from 'react';
import React, { useContext, useState } from 'react';
import { css, styled } from '@compiled/react';
import { useIntl, defineMessages } from 'react-intl-next';

import EditIcon from '@atlaskit/icon/core/edit';
import { token } from '@atlaskit/tokens';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Text, xcss } from '@atlaskit/primitives';
import Pressable from '@atlaskit/primitives/pressable';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { usePageLayoutResize } from '@atlaskit/page-layout';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import { SpotlightTarget } from '@atlaskit/onboarding';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';

import { fg } from '@confluence/feature-gating';
import { HoverPreloader, PreloadingPresets } from '@confluence/hover-preloader';
import { SPACE_INDEX, SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { EditSpaceLogo } from '@confluence/edit-space-logo';
import type { ExternalCollaboratorsSpaceInformationMessageType } from '@confluence/space-guest-list/entry-points/ExternalCollaboratorsSpaceInformationMessage.ts';
import { LoadableAfterPaint } from '@confluence/loadable';
import { useRouteName } from '@confluence/route-manager/entry-points/RouteState.ts';
import { EDIT_SPACE_LOGO_DETAILS_LOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { EditorSpaceSpotlight } from '@confluence/experiment-nth-user-orientation/entry-points/EditorSpaceSpotlight.ts';

import { ScrollToTopButton } from './ScrollToTopButton.tsx';
import { SpaceMoreActionsMenu } from './SpaceMoreActionsMenu.tsx';

const ExternalCollaboratorsSpaceInformationMessageLoader = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ExternalCollaboratorsSpaceInformationMessage" */ '@confluence/space-guest-list/entry-points/ExternalCollaboratorsSpaceInformationMessage.ts'
			)
		).ExternalCollaboratorsSpaceInformationMessage,
	name: 'ExternalCollaboratorsSpaceInformationMessage',
});

const RemovedSpotlightTarget = componentWithFG(
	'remove_unused_spotlight_targets',
	({ children }: PropsWithChildren<{}>) => <>{children}</>,
	({ name, children }: { name: string } & PropsWithChildren<{}>) => (
		<SpotlightTarget name={name}>{children}</SpotlightTarget>
	),
);

export type SpaceHeaderProps = {
	spaceId: string;
	spaceName: string;
	spaceKey: string;
	homepageId: string | undefined;
	iconPath: string | undefined;
	isStarred: boolean;
	isWatched: boolean;
	isSpaceAdmin: boolean;
	canManageSomeUsers: boolean;
	isPersonalSpace: boolean;
	isSpaceArchived: boolean;
	hasDeleteSpacePermission: boolean;
	hasArchiveSpacePermission: boolean;
};

export type ExternalCollaboratorProps = {
	isExternalCollaborator: boolean;
	containsExternalCollaborators: boolean;
};

export const SpaceHeader = ({
	isExternalCollaborator,
	containsExternalCollaborators,
	...props
}: SpaceHeaderProps & ExternalCollaboratorProps) => {
	const showExCoMessaging = !isExternalCollaborator && containsExternalCollaborators;

	const header = <SpaceHeaderInner {...props} showExCoMessaging={showExCoMessaging} />;

	return header;
};

const SpaceHeaderInner = ({
	spaceId,
	spaceName,
	spaceKey,
	homepageId,
	iconPath,
	isStarred,
	isWatched,
	isSpaceAdmin,
	canManageSomeUsers,
	isPersonalSpace,
	showExCoMessaging,
	isSpaceArchived,
	hasDeleteSpacePermission,
	hasArchiveSpacePermission,
}: SpaceHeaderProps & { showExCoMessaging: boolean }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isSelected = useRouteName(isOnSpaceRouteArgs);

	const spaceOverviewHref = SPACE_OVERVIEW.toUrl(
		{ spaceKey },
		homepageId
			? {
					query: { [SPACE_OVERVIEW.HOMEPAGE_ID_QUERY_KEY]: homepageId },
				}
			: undefined,
	);

	const description = showExCoMessaging ? (
		<Text weight="medium">
			<ExternalCollaboratorsSpaceInformationMessageLoader
				analyticsSource="spaceHeader"
				messageType={'IN_SPACE' as ExternalCollaboratorsSpaceInformationMessageType}
				shouldShowMessageIcon={false}
				shouldShowAdditionalMessageContext={false}
				spaceKey={spaceKey}
				isSubtle
			/>
		</Text>
	) : undefined;

	const onClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'spaceHeader',
				source: 'sideNavigation',
				attributes: {
					navVersion: 4,
				},
			},
		}).fire();
	};

	const [isMoreActionsOpen, setIsMoreActionsOpen] = useState(false);

	const moreActionsMenu = (
		<SpaceMoreActionsMenu
			isOpen={isMoreActionsOpen}
			setIsOpen={setIsMoreActionsOpen}
			spaceId={spaceId}
			spaceKey={spaceKey}
			isStarred={isStarred}
			isWatched={isWatched}
			isSpaceAdmin={isSpaceAdmin}
			canManageSomeUsers={canManageSomeUsers}
			isSpaceArchived={isSpaceArchived}
			hasDeleteSpacePermission={hasDeleteSpacePermission}
			hasArchiveSpacePermission={hasArchiveSpacePermission}
		/>
	);

	return (
		<>
			<Box xcss={stickyWrapper} data-vc="Nav4-space-header">
				<SpaceHeaderContainer>
					<EditorSpaceSpotlight>
						<RemovedSpotlightTarget name="nav4-spotlight-2">
							<HoverPreloader
								name="space-header"
								preloadUrl={spaceOverviewHref}
								preloadOptions={PreloadingPresets.onlyJSAndCriticalData}
							>
								<LinkMenuItem
									href={spaceOverviewHref}
									isSelected={isSelected}
									onClick={onClick}
									elemBefore={
										<EditableSpaceIcon
											spaceId={spaceId}
											spaceKey={spaceKey}
											iconPath={iconPath}
											isSpaceAdmin={isSpaceAdmin}
											isPersonalSpace={isPersonalSpace}
										/>
									}
									actions={moreActionsMenu}
									// @ts-expect-error Platform component current only accepts a string; we're passing an element anyway.
									description={description}
								>
									{spaceName}
								</LinkMenuItem>
							</HoverPreloader>
						</RemovedSpotlightTarget>
					</EditorSpaceSpotlight>
				</SpaceHeaderContainer>
			</Box>
			{/**
			 * this is the scroll to top button
			 **/}
			<ScrollToTopButton showExCoMessaging={showExCoMessaging} />
		</>
	);
};

type EditableSpaceIconProps = Pick<
	SpaceHeaderProps,
	'spaceId' | 'spaceKey' | 'iconPath' | 'isSpaceAdmin' | 'isPersonalSpace'
>;

const EditableSpaceIcon = ({
	spaceId,
	spaceKey,
	iconPath,
	isSpaceAdmin,
	isPersonalSpace,
}: EditableSpaceIconProps) => {
	const intl = useIntl();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { leftSidebarState, expandLeftSidebar } = usePageLayoutResize();

	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [shouldRenderDialog, setShouldRenderDialog] = useState(false);

	const image = iconPath ? (
		<img
			src={iconPath}
			alt=""
			css={icon}
			data-testid="space-icon"
			data-vc="space-header-icon-image"
			data-ssr-placeholder-ignored
			role="presentation"
		/>
	) : null;

	const canEdit = isSpaceAdmin && !isPersonalSpace;
	if (!canEdit) {
		return image;
	}

	const editLabel = intl.formatMessage(i18n.editSpaceDetails);

	const onClick = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		experienceTracker.start({
			name: EDIT_SPACE_LOGO_DETAILS_LOAD_EXPERIENCE,
			timeout: 10000,
		});

		setShouldRenderDialog(true);
		setIsDialogOpen(true);

		if (leftSidebarState.isFlyoutOpen) {
			expandLeftSidebar();
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'editSpaceDetails',
				source: 'sideNavSpaceHeader',
				containerType: 'space',
				containerId: spaceId,
				attributes: {
					navVersion: 4,
					entryPoint: 'sideNav',
					spaceKey,
				},
			},
		}).fire();
	};

	const onFocus = () => {
		setShouldRenderDialog(true);
	};

	const onMouseEnter = () => {
		setShouldRenderDialog(true);
	};

	const onDialogOpen = () => {
		setIsDialogOpen(true);
	};

	const onDialogClose = () => {
		setIsDialogOpen(false);
	};

	return (
		<>
			<Tooltip content={editLabel}>
				<Pressable
					xcss={fg('confluence_frontend_tokenize_border') ? iconButton : iconButtonFallback}
					aria-label={editLabel}
					onClick={onClick}
					onFocus={onFocus}
					onMouseEnter={onMouseEnter}
				>
					{image}
					<Box xcss={editIconWrapper}>
						<EditIcon label="" color={token('color.icon.inverse')} />
					</Box>
				</Pressable>
			</Tooltip>
			{shouldRenderDialog && (
				<EditSpaceLogo
					spaceKey={spaceKey}
					isOpen={isDialogOpen}
					onOpen={onDialogOpen}
					onClose={onDialogClose}
					entryPoint="sideNav"
				/>
			)}
		</>
	);
};

const isOnSpaceRouteArgs = {
	selector: (routeName: string | undefined) =>
		routeName === SPACE_INDEX.name || routeName === SPACE_OVERVIEW.name,
};

const stickyWrapper = xcss({
	position: 'sticky',
	top: 'space.0',
	zIndex: 'navigation', // Needs to be at least z-index 2 to appear on top of Content and Shortcuts
});

const icon = css({
	width: '24px',
	height: '24px',
	borderRadius: token('radius.medium'),
});

const iconButton = xcss({
	border: token('space.0'),
	borderRadius: 'radius.small',
	background: 'none',
	padding: 'space.0',
	position: 'relative',
	width: '24px',
	height: '24px',
	cursor: 'pointer',
	marginTop: 'space.050',
});

const iconButtonFallback = xcss({
	border: 'none',
	borderRadius: 'radius.small',
	background: 'none',
	padding: 'space.0',
	position: 'relative',
	width: '24px',
	height: '24px',
	cursor: 'pointer',
	marginTop: 'space.050',
});

const editIconWrapper = xcss({
	position: 'absolute',
	height: '24px',
	width: '24px',
	top: 'space.0',
	left: 'space.0',
	padding: 'space.050',
	opacity: 0,
	transition: 'opacity 0.5s ease',
	':hover': {
		opacity: 1,
	},
	':focus': {
		opacity: 1,
	},
	backgroundColor: 'color.blanket',
	borderRadius: 'radius.medium',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceHeaderContainer = styled.div({
	// The padding here is weird because of the sticky/absolute borders.
	// The padding top and bottom 'should' be 12px/space.150, but the borders add 1px each, so the padding is actually 11px.
	// But the top border is positioned on top of the element, so the top padding must stay 12px to vertically center the content.
	// But the bottom border is positioned below the element, so the bottom padding must be 11px otherwise the border will be hidden.

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	paddingTop: token('space.150'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingBottom: '11px',
	backgroundColor: token('elevation.surface'),
});

const i18n = defineMessages({
	editSpaceDetails: {
		id: 'side-navigation.space-navigation.header.edit-space-details',
		defaultMessage: 'Edit space details',
		description: 'Text for a tooltip and label indicating where to click to edit space details',
	},
});
